import { useEffect, useMemo, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { SectionEyebrow } from "@/components/Primitives";
import SEO from "@/lib/seo";
import { ArrowLeft, ArrowRight, CheckCircle2, Circle, PlayCircle, BookOpen, FileQuestion, Lock } from "lucide-react";
import { toast } from "sonner";

const TYPE_ICON = { video: PlayCircle, reading: BookOpen, quiz: FileQuestion };

export default function CourseLearn() {
  const { enrolmentId } = useParams();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [enr, setEnr] = useState(null);
  const [course, setCourse] = useState(null);
  const [activeIdx, setActiveIdx] = useState(0);
  const [quizAnswers, setQuizAnswers] = useState({});
  const [submittingQuiz, setSubmittingQuiz] = useState(false);
  const [quizFeedback, setQuizFeedback] = useState(null);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate("/login?next=" + encodeURIComponent(`/dashboard/courses/${enrolmentId}`)); return; }
    (async () => {
      try {
        const { data: enrolments } = await api.get("/me/enrolments");
        const found = enrolments.find((e) => e.id === enrolmentId);
        if (!found) { toast.error("Enrolment not found"); navigate("/dashboard"); return; }
        setEnr(found);
        const { data: courseData } = await api.get(`/courses/${found.course_code}`);
        setCourse(courseData);
      } catch {
        toast.error("Could not load course");
        navigate("/dashboard");
      }
    })();
  }, [enrolmentId, user, authLoading, navigate]);

  const modules = useMemo(() => course?.module_content || [], [course]);
  const completed = useMemo(() => new Set(enr?.modules_completed || []), [enr]);
  const activeModule = modules[activeIdx];

  useEffect(() => { setQuizAnswers({}); setQuizFeedback(null); }, [activeIdx]);

  if (!enr || !course) return <div className="container-x section-y text-sm text-muted-foreground">Loading…</div>;

  const markCompleted = async (idx, completedFlag = true) => {
    try {
      const { data } = await api.post(`/me/enrolments/${enr.id}/progress`, { module_index: idx, completed: completedFlag });
      setEnr(data);
    } catch { toast.error("Could not update progress"); }
  };

  const submitQuiz = async () => {
    const answers = Array.from({ length: activeModule.quiz?.length || 0 }, (_, i) => quizAnswers[i] ?? -1);
    setSubmittingQuiz(true);
    try {
      const { data } = await api.post(`/me/enrolments/${enr.id}/quiz`, { module_index: activeIdx, answers });
      setQuizFeedback(data);
      if (data.passed) {
        toast.success(`Passed — ${data.score_pct}% (${data.correct}/${data.total})`);
        const { data: enrolments } = await api.get("/me/enrolments");
        const refreshed = enrolments.find((e) => e.id === enr.id);
        if (refreshed) setEnr(refreshed);
      } else {
        toast.error(`Not passed — ${data.score_pct}%. You need 80%. See the feedback below and try again.`);
      }
    } catch { toast.error("Could not submit quiz"); }
    setSubmittingQuiz(false);
  };

  const retryQuiz = () => { setQuizAnswers({}); setQuizFeedback(null); };

  return (
    <div data-testid="course-learn-page">
      <SEO title={`${course.title} · Learning · Cleanability Institute`} path={`/dashboard/courses/${enr.id}`} />
      <section className="container-x pt-16 md:pt-24 pb-8">
        <Link to="/dashboard" data-testid="back-to-dashboard" className="inline-flex items-center gap-2 text-xs font-medium text-muted-foreground hover:text-foreground">
          <ArrowLeft className="h-3.5 w-3.5" /> Dashboard
        </Link>
        <div className="mt-4 font-mono-mini text-[hsl(var(--accent))]">{course.code} · {course.duration}</div>
        <h1 className="mt-3 font-display text-3xl md:text-4xl lg:text-5xl tracking-tight font-medium text-balance">{course.title}</h1>
        <div className="mt-6 flex items-center gap-3 max-w-md">
          <div className="h-2 flex-1 rounded-full bg-border overflow-hidden">
            <div className="h-full bg-[hsl(var(--accent))] transition-all" style={{ width: `${enr.progress_pct || 0}%` }} />
          </div>
          <div className="font-display text-lg font-medium tracking-tight">{enr.progress_pct || 0}%</div>
        </div>
      </section>

      <section className="container-x pb-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          <aside className="lg:col-span-4" data-testid="modules-sidebar">
            <div className="rounded-2xl border border-border bg-card overflow-hidden sticky top-24">
              <div className="px-5 py-4 border-b border-border">
                <div className="font-mono-mini text-muted-foreground">Modules</div>
                <div className="mt-1 font-display text-lg font-medium tracking-tight">{modules.length} sessions</div>
              </div>
              {modules.length === 0 ? (
                <div className="p-5 text-sm text-muted-foreground">Module content is being prepared for this programme.</div>
              ) : (
                <ul className="divide-y divide-border">
                  {modules.map((m, i) => {
                    const isDone = completed.has(i);
                    const isActive = i === activeIdx;
                    const Icon = TYPE_ICON[m.type] || BookOpen;
                    return (
                      <li key={i}>
                        <button onClick={() => setActiveIdx(i)} data-testid={`module-tab-${i}`} className={`w-full px-5 py-4 flex items-start gap-3 text-left transition-colors ${isActive ? "bg-secondary/70" : "hover:bg-secondary/40"}`}>
                          <div className="mt-0.5">
                            {isDone ? <CheckCircle2 className="h-4 w-4 text-[hsl(var(--accent))]" /> : <Circle className="h-4 w-4 text-muted-foreground" />}
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="text-[10px] font-mono-mini text-muted-foreground uppercase flex items-center gap-1.5">
                              <Icon className="h-3 w-3" /> Module {i + 1} · {m.type}
                            </div>
                            <div className="mt-1 font-medium text-sm truncate">{m.title}</div>
                          </div>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </aside>

          <main className="lg:col-span-8">
            {!activeModule ? (
              <div className="rounded-2xl border border-border bg-card p-10 text-sm text-muted-foreground">No module selected.</div>
            ) : (
              <article data-testid={`module-content-${activeIdx}`} className="rounded-2xl border border-border bg-card p-8 md:p-12">
                <div className="font-mono-mini text-[hsl(var(--accent))] uppercase">Module {activeIdx + 1} · {activeModule.type}</div>
                <h2 className="mt-3 font-display text-2xl md:text-4xl tracking-tight font-medium text-balance">{activeModule.title}</h2>

                {activeModule.type === "video" && activeModule.video_url && (
                  <div className="mt-8 rounded-2xl border border-border overflow-hidden aspect-video">
                    <iframe data-testid="module-video" src={activeModule.video_url} title={activeModule.title} className="w-full h-full" allowFullScreen />
                  </div>
                )}

                {activeModule.body && (
                  <div className="mt-8 prose-essay text-base md:text-lg leading-relaxed text-foreground/85" data-testid="module-body">
                    <ReactMarkdown
                      components={{
                        h2: (p) => <h2 className="font-display text-xl md:text-2xl tracking-tight font-medium mt-8 mb-3" {...p} />,
                        h3: (p) => <h3 className="font-display text-lg md:text-xl tracking-tight font-medium mt-6 mb-2" {...p} />,
                        ul: (p) => <ul className="list-disc pl-6 space-y-2" {...p} />,
                        strong: (p) => <strong className="text-foreground font-semibold" {...p} />,
                      }}
                    >{activeModule.body}</ReactMarkdown>
                  </div>
                )}

                {activeModule.type === "quiz" && activeModule.quiz?.length > 0 && (
                  <div className="mt-10 space-y-6" data-testid="module-quiz">
                    {activeModule.quiz.map((q, qi) => {
                      const fb = quizFeedback?.feedback?.[qi];
                      return (
                        <div key={qi} className={`rounded-2xl border p-6 ${fb ? (fb.is_correct ? "border-[hsl(var(--accent))] bg-[hsl(var(--accent))]/5" : "border-destructive/40 bg-destructive/5") : "border-border bg-secondary/30"}`}>
                          <div className="font-mono-mini text-muted-foreground flex items-center justify-between">
                            <span>Question {qi + 1}</span>
                            {fb && (
                              <span className={fb.is_correct ? "text-[hsl(var(--accent))]" : "text-destructive"} data-testid={`quiz-feedback-${qi}`}>
                                {fb.is_correct ? "Correct" : "Incorrect"}
                              </span>
                            )}
                          </div>
                          <div className="mt-2 font-display text-lg font-medium">{q.q}</div>
                          <div className="mt-4 space-y-2">
                            {q.options.map((o, oi) => {
                              const selected = quizAnswers[qi] === oi;
                              const isUserChoice = fb && fb.selected === oi;
                              const isCorrectOption = fb && fb.correct_index === oi;
                              let cls = "border-border bg-card hover:border-foreground/40";
                              if (fb) {
                                if (isCorrectOption) cls = "border-[hsl(var(--accent))] bg-[hsl(var(--accent))]/10";
                                else if (isUserChoice) cls = "border-destructive bg-destructive/10";
                                else cls = "border-border bg-card opacity-70";
                              } else if (selected) cls = "border-[hsl(var(--accent))] bg-card";
                              return (
                                <label key={oi} className={`flex items-center gap-3 px-4 py-3 rounded-md border transition-colors ${fb ? "cursor-default" : "cursor-pointer"} ${cls}`}>
                                  <input
                                    type="radio"
                                    name={`q-${qi}`}
                                    className="accent-[hsl(var(--accent))]"
                                    checked={selected}
                                    disabled={!!fb}
                                    onChange={() => setQuizAnswers({ ...quizAnswers, [qi]: oi })}
                                    data-testid={`quiz-option-${qi}-${oi}`}
                                  />
                                  <span className="text-sm flex-1">{o}</span>
                                  {fb && isCorrectOption && <span className="text-[10px] font-mono-mini text-[hsl(var(--accent))]">correct answer</span>}
                                  {fb && isUserChoice && !isCorrectOption && <span className="text-[10px] font-mono-mini text-destructive">your answer</span>}
                                </label>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                    {!quizFeedback ? (
                      <button onClick={submitQuiz} disabled={submittingQuiz} data-testid="submit-quiz" className="inline-flex items-center gap-2 h-11 px-5 rounded-md bg-foreground text-background text-sm font-medium hover:opacity-90 disabled:opacity-60">
                        {submittingQuiz ? "Marking…" : "Submit answers"} <ArrowRight className="h-4 w-4" />
                      </button>
                    ) : (
                      <div className="flex flex-wrap items-center gap-3" data-testid="quiz-result">
                        <div className={`px-4 h-11 inline-flex items-center rounded-md font-display text-lg font-medium ${quizFeedback.passed ? "bg-[hsl(var(--accent))]/15 text-[hsl(var(--accent))]" : "bg-destructive/10 text-destructive"}`}>
                          {quizFeedback.score_pct}% — {quizFeedback.passed ? "Passed" : "Not passed"}
                        </div>
                        {!quizFeedback.passed && (
                          <button onClick={retryQuiz} data-testid="retry-quiz" className="inline-flex items-center gap-2 h-11 px-5 rounded-md bg-foreground text-background text-sm font-medium hover:opacity-90">
                            Retake quiz
                          </button>
                        )}
                        {quizFeedback.passed && activeIdx < modules.length - 1 && (
                          <button onClick={() => setActiveIdx(activeIdx + 1)} data-testid="next-after-quiz" className="inline-flex items-center gap-2 h-11 px-5 rounded-md border border-border text-sm font-medium hover:border-[hsl(var(--accent))]">
                            Next module <ArrowRight className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    )}
                    <p className="text-xs text-muted-foreground">Pass mark 80%. You may retry as often as needed.</p>
                  </div>
                )}

                {activeModule.type !== "quiz" && (
                  <div className="mt-10 pt-6 border-t border-border flex flex-wrap items-center justify-between gap-3">
                    <button onClick={() => markCompleted(activeIdx, !completed.has(activeIdx))} data-testid="toggle-complete" className="inline-flex items-center gap-2 h-11 px-5 rounded-md bg-foreground text-background text-sm font-medium hover:opacity-90">
                      {completed.has(activeIdx) ? "Mark as not completed" : "Mark as completed"}
                    </button>
                    {activeIdx < modules.length - 1 && (
                      <button onClick={() => setActiveIdx(activeIdx + 1)} data-testid="next-module" className="inline-flex items-center gap-2 h-11 px-5 rounded-md border border-border text-sm font-medium hover:border-[hsl(var(--accent))]">
                        Next module <ArrowRight className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                )}
              </article>
            )}
          </main>
        </div>
      </section>
    </div>
  );
}
